// src/app/[lang]/components/LazyFeaturedImage.tsx
"use client";

import React, { useState } from 'react';
import Image from 'next/image';

interface LazyFeaturedImageProps {
  src: string;
  alt: string;
  blurDataURL?: string;
  priority?: boolean;
  sizes: string;
  quality?: number;
}

/**
 * Featured article image with smooth transition from blur to full image
 * Includes hover scale effect for featured articles
 */
export default function LazyFeaturedImage({
  src,
  alt,
  blurDataURL,
  priority = false,
  sizes,
  quality = 75,
}: LazyFeaturedImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Ensure we have a properly formatted data URL
  const normalizedBlurDataURL = blurDataURL && !blurDataURL.startsWith('data:')
    ? `data:image/png;base64,${blurDataURL}`
    : blurDataURL;

  if (hasError) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <p className="text-gray-600 text-sm">Image failed to load</p>
      </div>
    );
  }

  return (
    <>
      {normalizedBlurDataURL && (
        <img
          src={normalizedBlurDataURL}
          alt=""
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-in-out group-hover:scale-[1.025] group-focus-visible:scale-[1.025]"
          aria-hidden="true"
        />
      )}
      <Image
        src={src}
        alt={alt}
        fill
        priority={priority}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        quality={quality}
        className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in-out group-hover:scale-[1.025] group-focus-visible:scale-[1.025] ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        sizes={sizes}
        onLoad={() => setIsLoaded(true)}
        onError={() => {
          console.error('Featured image failed to load:', src.substring(0, 50));
          setHasError(true);
        }}
      />
    </>
  );
}
